/**
 * @fileoverview Greeting messages utility for chat page
 * Provides random greeting messages with optional firstName interpolation
 */

export interface GreetingMessage {
  key: string;
  message: string;
  messageDE: string; // German translation
  messageIT: string; // Italian translation
  messageFR: string; // French translation
  useFirstName: boolean;
}

/**
 * Array of 10 different greeting messages
 * Some include firstName, others are general greetings
 */
export const GREETING_MESSAGES: GreetingMessage[] = [
  {
    key: 'GREETING_HELLO_FIRSTNAME',
    message: 'Hi {firstName}, how can I help?',
    messageDE: 'Hallo {firstName}, wie kann ich dir helfen?',
    messageIT: 'Ciao {firstName}, come posso aiutarti?',
    messageFR: 'Salut {firstName}, comment puis-je aider?',
    useFirstName: true,
  },
  {
    key: 'GREETING_EXCITING_DAY',
    message: "Hi! Ready for something fun?",
    messageDE: 'Hallo! Bereit für etwas Lustiges?',
    messageIT: 'Ciao! Pronto per qualcosa di divertente?',
    messageFR: 'Salut! Prêt pour quelque chose d\'amusant?',
    useFirstName: false,
  },
  {
    key: 'GREETING_WELCOME_FIRSTNAME',
    message: 'Welcome back, {firstName}!',
    messageDE: 'Willkommen zurück, {firstName}!',
    messageIT: 'Bentornato, {firstName}!',
    messageFR: 'Bienvenue, {firstName}!',
    useFirstName: true,
  },
  {
    key: 'GREETING_READY_TO_HELP',
    message: "Hi there! I'm ready to help.",
    messageDE: 'Hallo! Ich bin bereit zu helfen.',
    messageIT: 'Ciao! Sono pronto ad aiutare.',
    messageFR: 'Salut! Je suis prêt à aider.',
    useFirstName: false,
  },
  {
    key: 'GREETING_GREAT_DAY_FIRSTNAME',
    message: 'Good day, {firstName}!',
    messageDE: 'Guten Tag, {firstName}!',
    messageIT: 'Buongiorno, {firstName}!',
    messageFR: 'Bonjour, {firstName}!',
    useFirstName: true,
  },
  {
    key: 'GREETING_LETS_CREATE',
    message: "Let's create something great!",
    messageDE: 'Lass uns etwas Grossartiges erschaffen!',
    messageIT: 'Creiamo qualcosa di fantastico!',
    messageFR: 'Créons quelque chose de génial!',
    useFirstName: false,
  },
  {
    key: 'GREETING_NICE_TO_SEE_FIRSTNAME',
    message: 'Nice to see you, {firstName}!',
    messageDE: 'Schön dich wiederzusehen, {firstName}!',
    messageIT: 'Bello vederti, {firstName}!',
    messageFR: 'Content de te voir, {firstName}!',
    useFirstName: true,
  },
  {
    key: 'GREETING_READY_FOR_ADVENTURE',
    message: "Hello! Ready for an adventure?",
    messageDE: 'Hallo! Bist du bereit für ein Abenteuer?',
    messageIT: 'Ciao! Pronto per un\'avventura?',
    messageFR: 'Salut! Prêt pour une aventure?',
    useFirstName: false,
  },
  {
    key: 'GREETING_WONDERFUL_DAY_FIRSTNAME',
    message: 'Great day to chat, {firstName}!',
    messageDE: 'Ein toller Tag zum Chatten, {firstName}!',
    messageIT: 'Bella giornata per chattare, {firstName}!',
    messageFR: 'Belle journée pour discuter, {firstName}!',
    useFirstName: true,
  },
  {
    key: 'GREETING_LETS_GET_STARTED',
    message: "Hi {firstName} Let's get started!",
    messageDE: 'Hallo {firstName}, lass uns anfangen!',
    messageIT: 'Ciao {firstName} Iniziamo!',
    messageFR: 'Salut {firstName} Commençons!',
    useFirstName: true,
  },
];

/**
 * Gets a random greeting message
 * @param firstName - Optional first name to include in personalized greetings
 * @param language - Language code (e.g., 'en-US', 'de-DE', 'it-IT', 'fr-FR'). Defaults to 'en-US'
 * @returns A formatted greeting message
 */
export const getRandomGreeting = (firstName?: string, language = 'en-US'): string => {
  // Get a random message from the array
  const randomIndex = Math.floor(Math.random() * GREETING_MESSAGES.length);
  const selectedGreeting = GREETING_MESSAGES[randomIndex];
  
  // Determine which message to use based on language
  const isGerman = language.startsWith('de');
  const isItalian = language.startsWith('it');
  const isFrench = language.startsWith('fr');
  
  let messageText = selectedGreeting.message;
  if (isGerman) {
    messageText = selectedGreeting.messageDE;
  } else if (isItalian) {
    messageText = selectedGreeting.messageIT;
  } else if (isFrench) {
    messageText = selectedGreeting.messageFR;
  }
  
  // If the message uses firstName and we have a firstName, interpolate it
  if (selectedGreeting.useFirstName && firstName) {
    return messageText.replace('{firstName}', firstName);
  }
  
  // If the message uses firstName but we don't have one, fall back to a non-firstName message
  if (selectedGreeting.useFirstName && !firstName) {
    const nonFirstNameMessages = GREETING_MESSAGES.filter(msg => !msg.useFirstName);
    const fallbackIndex = Math.floor(Math.random() * nonFirstNameMessages.length);
    const fallbackGreeting = nonFirstNameMessages[fallbackIndex];
    
    let fallbackText = fallbackGreeting.message;
    if (isGerman) {
      fallbackText = fallbackGreeting.messageDE;
    } else if (isItalian) {
      fallbackText = fallbackGreeting.messageIT;
    } else if (isFrench) {
      fallbackText = fallbackGreeting.messageFR;
    }
    
    return fallbackText;
  }
  
  // Return the message as-is for non-firstName messages
  return messageText;
};

/**
 * Gets a specific greeting message by key
 * @param key - The greeting message key
 * @param firstName - Optional first name to include in personalized greetings
 * @param language - Language code (e.g., 'en-US', 'de-DE', 'it-IT', 'fr-FR'). Defaults to 'en-US'
 * @returns The formatted greeting message or null if key not found
 */
export const getGreetingByKey = (key: string, firstName?: string, language = 'en-US'): string | null => {
  const greeting = GREETING_MESSAGES.find(msg => msg.key === key);
  
  if (!greeting) {
    return null;
  }
  
  // Determine which message to use based on language
  const isGerman = language.startsWith('de');
  const isItalian = language.startsWith('it');
  const isFrench = language.startsWith('fr');
  
  let messageText = greeting.message;
  if (isGerman) {
    messageText = greeting.messageDE;
  } else if (isItalian) {
    messageText = greeting.messageIT;
  } else if (isFrench) {
    messageText = greeting.messageFR;
  }
  
  if (greeting.useFirstName && firstName) {
    return messageText.replace('{firstName}', firstName);
  }
  
  return messageText;
};
